import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { getArticle, ARTICLES } from "@/data/articles";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowLeft, Lightbulb, Footprints } from "lucide-react";

export default function Article() {
  const { slug } = useParams();
  const article = getArticle(slug);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} — Step Zone`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", article.excerpt);
    }
    return () => { document.title = "Step Zone — Take your next financial step"; };
  }, [article]);

  if (!article) return <Navigate to="/blog" replace />;

  const related = ARTICLES.filter((a) => a.slug !== article.slug && a.category === article.category).slice(0, 2);
  const fallback = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 2);
  const suggestions = related.length ? related : fallback;

  return (
    <PublicLayout>
      <article className="mx-auto max-w-3xl px-6 py-14 md:py-20 lg:px-8" data-testid="article-page">
        <Link to="/blog" className="inline-flex items-center gap-1 font-body text-sm font-semibold text-muted-foreground hover:text-primary" data-testid="back-to-blog">
          <ArrowLeft className="h-4 w-4" /> All articles
        </Link>

        <span className="mt-6 inline-block rounded-md bg-primary/10 px-2.5 py-1 font-body text-xs font-semibold uppercase tracking-wide text-primary">
          {article.category}
        </span>
        <h1 className="mt-4 font-heading text-3xl font-bold tracking-tighter text-foreground sm:text-4xl">
          {article.title}
        </h1>
        <p className="mt-4 font-body text-lg leading-relaxed text-muted-foreground">{article.excerpt}</p>

        <div className="mt-10 space-y-10">
          {article.sections.map((s, i) => (
            <motion.section
              key={s.heading}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.03 }}
            >
              <h2 className="font-heading text-xl font-semibold tracking-tight text-foreground">{s.heading}</h2>
              {s.body.map((p, j) => (
                <p key={j} className="mt-3 font-body leading-relaxed text-muted-foreground">{p}</p>
              ))}
            </motion.section>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border-2 border-primary/20 bg-primary/5 p-6" data-testid="article-takeaway">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Lightbulb className="h-5 w-5" />
            </span>
            <div>
              <p className="font-body text-xs font-semibold uppercase tracking-[0.15em] text-primary">Takeaway</p>
              <p className="mt-1 font-body leading-relaxed text-foreground">{article.takeaway}</p>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col items-start justify-between gap-4 rounded-2xl border border-border bg-card p-6 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Footprints className="h-5 w-5" />
            </span>
            <div>
              <p className="font-heading font-semibold text-foreground">Ready to take your next step?</p>
              <p className="font-body text-sm text-muted-foreground">Tell us your goal and choose how we reach you.</p>
            </div>
          </div>
          <Link to="/" data-testid="article-wizard-cta">
            <Button className="rounded-xl bg-primary px-6 py-5 font-heading font-semibold text-primary-foreground hover:bg-primary/90">
              Get started
            </Button>
          </Link>
        </div>

        <div className="mt-14">
          <p className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Keep reading</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {suggestions.map((a) => (
              <Link key={a.slug} to={`/blog/${a.slug}`} data-testid={`related-article-${a.slug}`} className="group rounded-xl border-2 border-border bg-card p-5 transition-colors hover:border-primary">
                <h3 className="font-heading font-semibold text-foreground group-hover:text-primary">{a.title}</h3>
                <p className="mt-1 font-body text-sm text-muted-foreground">{a.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </article>
    </PublicLayout>
  );
}
